import React, { useState } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import { LoadingState } from '../../../components/common/EmptyState';
import { Button } from '../../../components/ui/button';
import { TabsContent } from '../../../components/ui/tabs';
import { facturacionApi } from '../../../lib/api';
import { cn, formatImporte } from '../../../lib/utils';
import { AlertTriangle, Check, Trash2 } from 'lucide-react';
export const TabMorosos = ({ cn, edificioId, error, formatImporte, loadingMorosos, morososData, selectedPeriodo, setFacturacionData, setIndicadoresFacturacion, setMorososData, toast }) => {
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const periodoLabel = (p) => { const [y, m] = p.split('-').map(Number); return new Date(y, m - 1, 15).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' }); };
  const confirmarBorrado = async () => {
    setBorrando(true);
    try {
      await facturacionApi.anularMorosidad(edificioId, aBorrar.unidad_id, aBorrar.periodo);
      setMorososData(prev => ({ ...prev, items: prev.items.filter(i => !(i.unidad_id === aBorrar.unidad_id && i.periodo === aBorrar.periodo)) }));
      toast.success('Morosidad eliminada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la morosidad');
    } finally {
      setBorrando(false);
      setABorrar(null);
    }
  };
  return (
        <>
        <TabsContent value="morosos" className="mt-0">
          {loadingMorosos ? (
            <LoadingState text="Cargando facturas pendientes..." />
          ) : !morososData || morososData.items.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800">Sin morosos</h3>
              <p className="text-green-600 text-sm">Todas las facturas de meses anteriores han sido cobradas.</p>
            </div>
          ) : (
            <div className="space-y-4 flex-1 min-h-0 flex flex-col">
              {/* Indicadores */}
              {(() => {
                const hoy = new Date();
                let total = morososData.items.length;
                let mas60 = 0, mas30 = 0, menos30 = 0;
                morososData.items.forEach(item => {
                  const dias = item.dias_atraso || 0;
                  if (dias > 60) mas60++;
                  else if (dias > 30) mas30++;
                  else menos30++;
                });
                return (
                  <div className="flex items-center gap-4 flex-wrap text-sm px-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Pendientes:</span>
                      <span className="font-bold text-slate-900">{total}</span>
                    </div>
                    <div className="h-4 w-px bg-slate-300"></div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">{'>'}60 días:</span>
                      <span className={cn("font-bold", mas60 > 0 ? "text-red-600" : "text-slate-900")}>{mas60}</span>
                    </div>
                    <div className="h-4 w-px bg-slate-300"></div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">30-60 días:</span>
                      <span className={cn("font-bold", mas30 > 0 ? "text-orange-600" : "text-slate-900")}>{mas30}</span>
                    </div>
                    <div className="h-4 w-px bg-slate-300"></div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">{'<'}30 días:</span>
                      <span className={cn("font-bold", menos30 > 0 ? "text-amber-600" : "text-slate-900")}>{menos30}</span>
                    </div>
                  </div>
                );
              })()}
              
              {/* Tabla de morosos */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-auto flex-1 min-h-0">
                <table className="w-full text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_tfoot_td]:sticky [&_tfoot_td]:bottom-0 [&_tfoot_td]:z-10 [&_tfoot_td]:bg-slate-100">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-slate-700">Unidad</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-700">Empresa</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-700">Período</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Días Atraso</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Deuda Alquiler</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Intereses (1%)</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-700">Total</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...morososData.items]
                      .sort((a, b) => {
                        // Primero ordenar por unidad (números primero, luego letras)
                        const nombreA = a.unidad_nombre || '';
                        const nombreB = b.unidad_nombre || '';
                        const esNumeroA = /^\d/.test(nombreA);
                        const esNumeroB = /^\d/.test(nombreB);
                        
                        if (esNumeroA && !esNumeroB) return -1;
                        if (!esNumeroA && esNumeroB) return 1;
                        
                        const comparacionUnidad = nombreA.localeCompare(nombreB, 'es', { numeric: true });
                        if (comparacionUnidad !== 0) return comparacionUnidad;
                        
                        // Dentro de la misma unidad, ordenar por período (más viejo primero)
                        return a.periodo.localeCompare(b.periodo);
                      })
                      .map((item, idx) => {
                        const diasAtraso = item.dias_atraso || 0;
                        const deudaAlquiler = item.saldo_pendiente || item.total || 0;
                        const penalidad = item.intereses || 0;
                        const totalDeuda = deudaAlquiler + penalidad;
                        
                        return (
                          <tr key={`${item.unidad_id}-${item.periodo}-${idx}`} className="hover:bg-slate-50/50">
                            <td className="py-2 px-3 font-medium text-slate-900">{item.unidad_nombre}</td>
                            <td className="py-2 px-3 text-slate-600">{item.inquilino || '-'}</td>
                            <td className="py-2 px-3">
                              <span className="text-slate-700">
                                {(() => {
                                  const [y, m] = item.periodo.split('-').map(Number);
                                  return new Date(y, m - 1, 15).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' });
                                })()}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right font-mono whitespace-nowrap">
                              {diasAtraso > 0 ? (
                                <span className={cn(
                                  "font-semibold",
                                  diasAtraso > 90 ? "text-red-700" : diasAtraso > 30 ? "text-orange-600" : "text-amber-600"
                                )}>
                                  {diasAtraso} días
                                </span>
                              ) : '-'}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-slate-700 whitespace-nowrap">
                              $ {formatImporte(deudaAlquiler, 2)}
                            </td>
                            <td className="py-2 px-3 text-right font-mono whitespace-nowrap">
                              {penalidad > 0 ? (
                                <span className="text-orange-600">$ {formatImporte(penalidad, 2)}</span>
                              ) : '-'}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-semibold text-red-600 whitespace-nowrap">
                              $ {formatImporte(totalDeuda, 2)}
                            </td>
                            <td className="py-2 px-1 text-right">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                                onClick={() => setABorrar(item)} title="Borrar morosidad"
                                data-testid={`moroso-borrar-${item.unidad_id}-${item.periodo}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  {(() => {
                    let sumDeuda = 0, sumPenalidad = 0;
                    morososData.items.forEach(item => {
                      sumDeuda += item.saldo_pendiente || item.total || 0;
                      sumPenalidad += item.intereses || 0;
                    });
                    return (
                      <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                        <tr className="font-semibold">
                          <td colSpan={3} className="py-3 px-3"></td>
                          <td className="py-3 px-3 text-right text-xs text-slate-500 uppercase">Totales</td>
                          <td className="py-3 px-3 text-right font-mono text-slate-900 whitespace-nowrap">$ {formatImporte(sumDeuda, 2)}</td>
                          <td className="py-3 px-3 text-right font-mono text-orange-600 whitespace-nowrap">$ {formatImporte(sumPenalidad, 2)}</td>
                          <td className="py-3 px-3 text-right font-mono text-red-700 whitespace-nowrap">$ {formatImporte(sumDeuda + sumPenalidad, 2)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            </div>
          )}
        </TabsContent>
        <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Borrar esta morosidad?</AlertDialogTitle>
              <AlertDialogDescription>
                {aBorrar && <>Se quitará <b>{aBorrar.unidad_nombre}</b> · {aBorrar.inquilino} · <b>{periodoLabel(aBorrar.periodo)}</b> de Morosos y dejará de generar intereses. La factura del mes no se modifica.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="moroso-borrar-cancelar">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmarBorrado} disabled={borrando} className="bg-rose-600 hover:bg-rose-700" data-testid="moroso-borrar-confirmar">
                {borrando ? 'Borrando…' : 'Borrar morosidad'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </>
  );
};
